import { UseFormReturn } from 'react-hook-form'
import { MintFormSchema } from './validators'
import { MarketInfo } from '@/domain/market-info/marketInfo'
import { DialogFormNormalizedData, normalizeDialogFormValues } from '@/features/dialogs/common/logic/form'
import { useDebounce } from '@/utils/useDebounce'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { FormFieldsForDialog } from './types'
import { TokenWithBalance } from '@/domain/common/types'
import { MarketWalletInfo } from '@/domain/wallet/useMarketWalletInfo'
import { formFormat } from '@/domain/common/format'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'

interface DebouncedMintFormValues {
  fromAsset: DialogFormNormalizedData
  toAsset: DialogFormNormalizedData
}

interface UseDebouncedMintFormValuesArgs {
  form: UseFormReturn<MintFormSchema>
  marketInfo: MarketInfo
}

interface UseDebouncedMintFormValuesResult {
  debouncedFormValues: DebouncedMintFormValues
  isFormValid: boolean
  isDebouncing: boolean
}

function getNormalizedMintFormValuesKey(values: DebouncedMintFormValues): string {
  const from = [values.fromAsset.token.address, values.fromAsset.value.toFixed(), values.fromAsset.isMaxSelected].join(
    '-',
  )
  const to = [values.toAsset.token.address, values.toAsset.value.toFixed(), values.toAsset.isMaxSelected].join('-')
  return `${from}_${to}`
}

export function useDebouncedMintFormValues({
  form,
  marketInfo,
}: UseDebouncedMintFormValuesArgs): UseDebouncedMintFormValuesResult {
  const formValues = form.watch()

  const normalizedFormValues = {
    fromAsset: normalizeDialogFormValues(formValues.fromAsset, marketInfo),
    toAsset: normalizeDialogFormValues(formValues.toAsset, marketInfo),
  }

  const isFormValid = form.formState.isValid
  const { debouncedValue, isDebouncing } = useDebounce(
    { formValues: normalizedFormValues, isFormValid },
    getNormalizedMintFormValuesKey(normalizedFormValues) + isFormValid.toString(),
  )

  return {
    debouncedFormValues: debouncedValue.formValues,
    isFormValid: debouncedValue.isFormValid,
    isDebouncing,
  }
}

interface GetFormFieldsForMintFormParams {
  form: UseFormReturn<MintFormSchema>
  marketInfo: MarketInfo
  fromAssets: TokenWithBalance[]
  toAssets: TokenWithBalance[]
  walletInfo: MarketWalletInfo
}

export function getFormFieldsForMintForm({
  form,
  fromAssets,
  toAssets,
  marketInfo,
  walletInfo,
}: GetFormFieldsForMintFormParams): FormFieldsForDialog {
  const changeFromAsset = (newSymbol: TokenSymbol): void => {
    form.setValue('fromAsset.symbol', newSymbol)
    form.setValue('fromAsset.value', '')
    form.clearErrors()
  }

  const changeToAsset = (newSymbol: TokenSymbol): void => {
    form.setValue('toAsset.symbol', newSymbol)
    form.setValue('toAsset.value', '')
    form.clearErrors()
  }

  const changeFromValue = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value
    form.setValue('fromAsset.value', value)
    form.setValue('toAsset.value', value)
    form.trigger('fromAsset')
  }

  const { fromAsset, toAsset } = form.getValues()

  const fromReserve = marketInfo.findReserveBySymbol(TokenSymbol(fromAsset.symbol))
  const toReserve = marketInfo.findReserveBySymbol(TokenSymbol(toAsset.symbol))

  const onSetMax = (max: NormalizedUnitNumber): void => {
    const fromDecimals = fromReserve?.token.decimals || 0
    const toDecimals = toReserve?.token.decimals || 0

    form.setValue('toAsset.value', formFormat(max, toDecimals < fromDecimals ? toDecimals : fromDecimals))
  }

  const fromBalance = walletInfo.findWalletBalanceForSymbol(TokenSymbol(fromAsset.symbol))
  const toBalance = walletInfo.findWalletBalanceForSymbol(TokenSymbol(toAsset.symbol))

  return {
    selectedFromAsset: {
      value: fromAsset.value,
      token: marketInfo.findOneTokenBySymbol(TokenSymbol(fromAsset.symbol)),
      balance: fromBalance,
    },
    selectedToAsset: {
      value: toAsset.value,
      token: marketInfo.findOneTokenBySymbol(TokenSymbol(toAsset.symbol)),
      balance: toBalance,
    },
    fromAssets,
    toAssets,
    maxSelectedFieldName: 'isMaxSelected',
    changeFromAsset,
    changeToAsset,
    changeFromValue,
    onSetMax,
    maxValue: fromBalance,
    displayBalance: fromBalance,
  }
}
